import React, { useState } from "react";
import { Button, Loading, Modal } from "carbon-components-react";
import { Launch16 } from "@carbon/icons-react";

import { AdditionalIncomeShape, AppShape, DocShape } from "@eave/shared/types/api";
import { formatCurrency } from "@eave/shared/utils/currency";

import { LoadingState } from "underwriter_dashboard/shared/types";
import BaseTable, { EditingState } from "web_modules/shared/BaseTable";

import { addOtherIncome, updateAdditionalIncome } from "../api-helpers";
import OtherIncomeModalForm from "../forms/OtherIncomeModalForm";
import { IncomeSectionFields, validIncomeOtherDocs } from "../data-helpers";
import { calculateMonthlyIncomeForOtherIncomes, IncomeTableRow } from "../shared";
import { makeHeaderData } from "web_modules/shared/BaseTable/helpers";

interface Props {
  app: AppShape;
  borrowerEntity: number;
  allDocs: DocShape[];
  additionalIncome: any[];
  loadApp: Function;
  expandOverride: boolean;
}

export type AdditionalIncomeForm = AdditionalIncomeShape & IncomeSectionFields;

const OtherIncomeTable: React.FC<Props> = ({
  app,
  borrowerEntity,
  allDocs,
  additionalIncome,
  loadApp,
  expandOverride,
}: Props) => {
  const monthlyTotal = calculateMonthlyIncomeForOtherIncomes(additionalIncome);

  // For the modal form
  const initialValues: AdditionalIncomeForm = {
    associated_document_ids: [],
    previous_associated_document_ids: [],
    borrower_entity: borrowerEntity,
    document_name: "",
    expanded_data: null,
    loan_application_id: app.id,
    description: "",
    operator_income_type: "",
    redirect_to_documents_hurdle_for_edits: null,
    include_in_other: true,
    include_in_ratios: true,
    notes: "",
  };

  const [modalFormIsOpen, setModalFormOpen] = useState(false);
  const [formState, setFormState] = useState<AdditionalIncomeForm>(initialValues);

  const [editingMode, updateEditingMode] = useState(EditingState.CREATE);
  const [isLoading, setIsLoading] = useState(LoadingState.INACTIVE);

  if (isLoading === LoadingState.ACTIVE) {
    return <Loading active withOverlay={false} />;
  }

  const handleSubmit = async (values: AdditionalIncomeForm) => {
    setIsLoading(LoadingState.ACTIVE);
    if (editingMode === EditingState.CREATE) {
      await addOtherIncome(values, app.all_documents);
    }
    if (editingMode === EditingState.UPDATE) {
      const {
        id,
        borrower_entity,
        include_in_other,
        include_in_ratios,
        loan_application_id,
        notes,
        operator_income_type,

        associated_document_ids,
        previous_associated_document_ids,
      } = values;

      const payload = {
        id,
        borrower_entity,
        include_in_other,
        include_in_ratios,
        loan_application_id,
        notes,
        operator_income_type,
      };

      const docs_to_attach = associated_document_ids
        .filter((id: number) => !previous_associated_document_ids.includes(id))
        .map((id: number) => {
          return allDocs.find(doc => doc.id === id);
        });

      const docs_to_dettach = previous_associated_document_ids
        .filter(id => !associated_document_ids.includes(id))
        .map((id: number) => {
          return allDocs.find(doc => doc.id === id);
        });

      await updateAdditionalIncome(payload, docs_to_attach, docs_to_dettach);
    }
    setModalFormOpen(false);
    loadApp(app.id);
    setIsLoading(LoadingState.FINISHED);
  };

  // We want documents that are used, of type 'income_other', and that have positive monthly amounts
  const otherIncomeDocs = validIncomeOtherDocs(allDocs);

  // A function that wraps the setFormState func
  // that also sets some values to help us handle the API calls
  const updateFormState = (formData: AdditionalIncomeForm) => {
    // By also passing 'previous_associated_document_id', we can detect if the associated doc changed
    const dataWithHelperFields: AdditionalIncomeForm = {
      ...formData,
      previous_associated_document_ids: formData.associated_document_ids,
    };
    setFormState(dataWithHelperFields);
  };

  return (
    <>
      <Modal
        hasForm
        passiveModal
        open={modalFormIsOpen}
        modalHeading={
          editingMode === EditingState.CREATE ? "Add Other Income" : "Edit Other Income"
        }
        onRequestClose={() => {
          setFormState(initialValues);
          setModalFormOpen(false);
        }}
        onBlur={() => {
          setFormState(initialValues);
          setModalFormOpen(false);
        }}
      >
        <>
          {formState && !formState.redirect_to_documents_hurdle_for_edits && (
            <OtherIncomeModalForm
              otherIncomeDocs={otherIncomeDocs}
              onSubmit={handleSubmit}
              formState={formState} // Pass the form state for when we edit fields
              borrowers={app.borrowers}
              editingMode={editingMode}
              onRequestClose={() => {
                setFormState(initialValues);
                setModalFormOpen(false);
              }}
            />
          )}
          {formState && formState.redirect_to_documents_hurdle_for_edits === true && (
            <div>
              <h4 style={{ marginBottom: "10px" }}>
                This income source is ingested from a document.
              </h4>
              <p style={{ marginBottom: "20px" }}>
                To edit this data, open the document in the DocumentsHurdle by clicking below.
              </p>

              <ul>
                {formState?.allAssociatedDocuments.map(doc => (
                  <li>
                    <Button
                      kind="ghost--tertiary"
                      target="_blank"
                      rel="noreferrer"
                      href={`/underwriter/app/${app.id}/DocumentsHurdle?d=${doc.id}`}
                      renderIcon={Launch16}
                      style={{ whiteSpace: "nowrap", maxWidth: "unset" }}
                    >
                      Open{" "}
                      <strong>
                        &nbsp;{doc.revised_filename || doc.original_filename}&nbsp;({doc.type})
                      </strong>{" "}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      </Modal>

      <BaseTable
        key={`borrower-other-income-${borrowerEntity}`}
        useExpandedRows
        title={`Other Income (Total: ${formatCurrency(monthlyTotal.toFixed(2), 2, 2)})`}
        disableBatchEdit
        allowHeaderTextWrapping
        openModal={() => setModalFormOpen(true)}
        updateFormState={updateFormState}
        updateEditingMode={updateEditingMode}
        headerData={makeHeaderData({
          heading: "Disclosed Description",
          operator_income_type: "Actual Description",
          monthly_total: "Monthly Amount",
          document_name: "Document Name(s)",
        })}
        rawRowData={additionalIncome as any}
        rowData={additionalIncome.map((i: IncomeTableRow) => ({
          ...i,
          monthly_total: `${formatCurrency(i.monthly_total, 2, 2)}${
            !i.excludeFromTotal ? "" : " (Excluded from total)"
          }`,
        }))}
        expandOverride={expandOverride}
        collapseOverride={additionalIncome.length === 0}
        disableBatch
        disableOverflowDelete
      />
    </>
  );
};

export default OtherIncomeTable;
